import { App } from 'antd';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SITE } from 'config/config';
import {
  getWorldCupChampionStats,
  getWorldCupFinalAsiaTeamStats,
  getWorldCupPlayersList,
  getWorldCupTeamsAsiaList,
  getWorldCupTeamsList,
  getWorldCupTopScorerStats,
  predictChampion,
  predictFinalAsiaTeam,
  predictTopScorer
} from 'service/base';
import { LocalStorageKey } from 'utils/common/const';

import { ChampionPredictionTab, PlayerPredictionTab } from './WorldCupPredictionTabs';

function unwrapApiList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.result)) return res.result;
  return [];
}

function mapTeamFromApi(t) {
  const codeStr = String(t?.code ?? '').trim();
  const id =
    t?.id != null && String(t.id).trim() !== ''
      ? String(t.id).trim()
      : codeStr.length > 0
        ? codeStr
        : t?._id != null
          ? String(t._id).trim()
          : String(t?.teamId ?? t?.isoCode ?? t?.countryCode ?? '').trim();
  const name = String(t?.name_vi ?? t?.name ?? t?.teamName ?? t?.title ?? t?.label ?? '').trim();
  let flag = t?.flag ?? t?.logo ?? t?.imageUrl ?? t?.image ?? t?.avatar ?? t?.photo ?? '';
  const flagCode = /^[a-z]{2}$/i.test(codeStr) ? codeStr : /^[a-z]{2}$/i.test(id) ? id : '';
  if (!flag && flagCode) {
    flag = `https://flagcdn.com/w80/${String(flagCode).toLowerCase()}.png`;
  }
  if (!id || !name) return null;
  const countryCode = /^[a-z]{2}$/i.test(codeStr)
    ? codeStr.toLowerCase()
    : /^[a-z]{2}$/i.test(id)
      ? String(id).toLowerCase()
      : undefined;
  return { id, name, flag: flag || undefined, countryCode };
}

function mapPlayerFromApi(p) {
  const id = String(p?.id ?? p?._id ?? p?.playerId ?? '').trim();
  const name = p?.name ?? p?.playerName ?? p?.fullName ?? '';
  const rawPhoto = p?.avatar ?? p?.photo ?? p?.image ?? p?.imageUrl ?? '';
  const photo =
    rawPhoto ||
    `https://ui-avatars.com/api/?size=128&name=${encodeURIComponent(name || '?')}&background=f8fafc&color=334155&bold=true`;
  const teamName = p?.teamName ?? p?.team_name ?? '';
  if (!id || !name) return null;
  return {
    id,
    name,
    photo: photo || undefined,
    teamName: teamName || undefined,
    teamId: p?.teamId ?? p?.team_id,
    countryCode: p?.countryCode ?? p?.country_code
  };
}

function mapTopScorerPredictionRow(r, index) {
  const id = String(r?.id ?? r?._id ?? r?.playerId ?? '').trim();
  const name = r?.name ?? r?.playerName ?? '';
  const photo = r?.avatar ?? r?.photo ?? r?.image ?? '';
  const countRaw = r?.count ?? r?.votes ?? r?.voteCount ?? 0;
  const votes =
    typeof countRaw === 'number' ? countRaw : Number(String(countRaw).replace(/\D/g, '')) || 0;
  if (!id || !name) return null;
  return {
    id,
    name,
    votes,
    photo: photo || undefined,
    percent: typeof r?.percent === 'number' ? r.percent : undefined,
    teamName: r?.teamName ?? r?.team_name
  };
}

function mapTeamsChampionsRow(r, index) {
  const code = String(r?.code ?? '').trim();
  const name = r?.name_vi || r?.name || r?.teamName || r?.title || r?.label || '';
  const votesRaw = r?.count ?? r?.votes ?? r?.voteCount ?? r?.score ?? 0;
  const votes =
    typeof votesRaw === 'number' ? votesRaw : Number(String(votesRaw).replace(/\D/g, '')) || 0;
  let flag = r?.flag ?? r?.logo ?? r?.imageUrl ?? r?.image ?? '';
  const idGuess = code || (r?.id ?? r?.teamId ?? r?.countryCode);
  const idStr = String(idGuess ?? '').trim();
  if (!flag && idStr && /^[a-z]{2}$/i.test(idStr)) {
    flag = `https://flagcdn.com/w80/${idStr.toLowerCase()}.png`;
  }
  if (!name) return null;
  const safeId = code || idStr || `champion-row-${index}`;
  return { id: safeId, name, votes, flag: flag || undefined, percent: r?.percent };
}

const WorldCupPredictionBlock = forwardRef(function WorldCupPredictionBlock(
  { form, disabled, IP, FP },
  ref
) {
  const { notification } = App.useApp();

  const [activeTab, setActiveTab] = useState('champion');
  const [wcLoading, setWcLoading] = useState(true);

  const [championTeams, setChampionTeams] = useState([]);
  const [championLeaderboard, setChampionLeaderboard] = useState([]);
  const [wcPlayers, setWcPlayers] = useState([]);
  const [topScorerLeaderboard, setTopScorerLeaderboard] = useState([]);
  const [asiaTeams, setAsiaTeams] = useState([]);
  const [asiaLeaderboard, setAsiaLeaderboard] = useState([]);

  const [championQuery, setChampionQuery] = useState('');
  const [isChampionAutoFilled, setIsChampionAutoFilled] = useState(false);
  const [championCountryFilter, setChampionCountryFilter] = useState('all');
  const [selectedChampionId, setSelectedChampionId] = useState(null);
  const [isChampionVoting, setIsChampionVoting] = useState(false);

  const [topScorerQuery, setTopScorerQuery] = useState('');
  const [isTopScorerAutoFilled, setIsTopScorerAutoFilled] = useState(false);
  const [topScorerCountryFilter, setTopScorerCountryFilter] = useState('all');
  const [selectedTopScorerId, setSelectedTopScorerId] = useState(null);
  const [isTopScorerVoting, setIsTopScorerVoting] = useState(false);

  const [asiaQuery, setAsiaQuery] = useState('');
  const [isAsiaAutoFilled, setIsAsiaAutoFilled] = useState(false);
  const [asiaCountryFilter, setAsiaCountryFilter] = useState('all');
  const [selectedAsiaId, setSelectedAsiaId] = useState(null);
  const [isAsiaVoting, setIsAsiaVoting] = useState(false);

  const topScorerPlayersList = useMemo(() => {
    if (!wcPlayers.length) return [];
    return wcPlayers.map((p) => ({
      ...p,
      photo:
        p.photo ||
        `https://ui-avatars.com/api/?size=128&name=${encodeURIComponent(p.name)}&background=f8fafc&color=334155&bold=true`
    }));
  }, [wcPlayers]);

  const championCountryOptions = useMemo(() => {
    const codes = new Set();
    championTeams.forEach((t) => {
      const c =
        t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : null);
      if (c) codes.add(c);
    });
    const opts = [{ value: 'all', label: 'Tất cả' }];
    const dn = new Intl.DisplayNames(['vi'], { type: 'region' });
    Array.from(codes)
      .sort()
      .forEach((c) => {
        let label = c.toUpperCase();
        try {
          label = dn.of(c.toUpperCase()) || label;
        } catch (_) {}
        opts.push({ value: c, label });
      });
    return opts;
  }, [championTeams]);

  const playerCountryOptions = useMemo(() => {
    const codes = new Set();
    topScorerPlayersList.forEach((p) => {
      if (p.countryCode) codes.add(String(p.countryCode).toLowerCase());
    });
    const opts = [{ value: 'all', label: 'Tất cả' }];
    const dn = new Intl.DisplayNames(['vi'], { type: 'region' });
    Array.from(codes)
      .sort()
      .forEach((c) => {
        let label = c.toUpperCase();
        try {
          label = dn.of(c.toUpperCase()) || label;
        } catch (_) {}
        opts.push({ value: c, label });
      });
    return opts;
  }, [topScorerPlayersList]);

  const asiaCountryOptions = useMemo(() => {
    const codes = new Set();
    asiaTeams.forEach((t) => {
      const c =
        t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : null);
      if (c) codes.add(c);
    });
    const opts = [{ value: 'all', label: 'Tất cả' }];
    const dn = new Intl.DisplayNames(['vi'], { type: 'region' });
    Array.from(codes)
      .sort()
      .forEach((c) => {
        let label = c.toUpperCase();
        try {
          label = dn.of(c.toUpperCase()) || label;
        } catch (_) {}
        opts.push({ value: c, label });
      });
    return opts;
  }, [asiaTeams]);

  const filteredChampionTeams = useMemo(() => {
    let list = isChampionAutoFilled
      ? championTeams
      : championTeams.filter((t) =>
          t.name.toLowerCase().includes(championQuery.trim().toLowerCase())
        );
    if (championCountryFilter !== 'all') {
      list = list.filter((t) => {
        const code =
          t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
        return code === championCountryFilter;
      });
    }
    return list;
  }, [isChampionAutoFilled, championTeams, championQuery, championCountryFilter]);

  const filteredTopScorerPlayers = useMemo(() => {
    let list = isTopScorerAutoFilled
      ? topScorerPlayersList
      : topScorerPlayersList.filter((p) =>
          p.name.toLowerCase().includes(topScorerQuery.trim().toLowerCase())
        );
    if (topScorerCountryFilter !== 'all') {
      list = list.filter(
        (p) => String(p.countryCode || '').toLowerCase() === topScorerCountryFilter
      );
    }
    return list;
  }, [isTopScorerAutoFilled, topScorerPlayersList, topScorerQuery, topScorerCountryFilter]);

  const filteredAsiaTeams = useMemo(() => {
    let list = isAsiaAutoFilled
      ? asiaTeams
      : asiaTeams.filter((t) => t.name.toLowerCase().includes(asiaQuery.trim().toLowerCase()));
    if (asiaCountryFilter !== 'all') {
      list = list.filter((t) => {
        const code =
          t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
        return code === asiaCountryFilter;
      });
    }
    return list;
  }, [isAsiaAutoFilled, asiaTeams, asiaQuery, asiaCountryFilter]);

  const selectedChampion =
    championTeams.find((t) => String(t.id) === String(selectedChampionId)) || null;
  const selectedTopScorer =
    topScorerPlayersList.find((p) => String(p.id) === String(selectedTopScorerId)) || null;
  const selectedAsia = asiaTeams.find((t) => String(t.id) === String(selectedAsiaId)) || null;

  const loadWcData = useCallback(async () => {
    setWcLoading(true);
    try {
      const [teamsRes, playersRes, championsRes, topRes, finalAsiaStatsRes, asiaTeamsRes] =
        await Promise.all([
          getWorldCupTeamsList(),
          getWorldCupPlayersList(),
          getWorldCupChampionStats(),
          getWorldCupTopScorerStats(),
          getWorldCupFinalAsiaTeamStats(),
          getWorldCupTeamsAsiaList()
        ]);

      const teams = unwrapApiList(teamsRes).map(mapTeamFromApi).filter(Boolean);
      if (teams.length) setChampionTeams(teams);

      const rawPlayers =
        playersRes?.code === 0 && Array.isArray(playersRes?.data)
          ? playersRes.data
          : unwrapApiList(playersRes);
      const players = rawPlayers.map(mapPlayerFromApi).filter(Boolean);
      if (players.length) setWcPlayers(players);

      const rawChampions =
        championsRes?.code === 0 && Array.isArray(championsRes?.data)
          ? championsRes.data
          : unwrapApiList(championsRes);
      const championRows = rawChampions
        .map((row, i) => mapTeamsChampionsRow(row, i))
        .filter(Boolean)
        .sort((a, b) => (b.votes || 0) - (a.votes || 0));
      if (championRows.length) setChampionLeaderboard(championRows);

      const rawTop =
        topRes?.code === 0 && Array.isArray(topRes?.data) ? topRes.data : unwrapApiList(topRes);
      const topRows = rawTop
        .map((row, i) => {
          const m = mapTopScorerPredictionRow(row, i);
          return m ? { m, i } : null;
        })
        .filter(Boolean)
        .sort((a, b) => {
          const dv = (b.m.votes || 0) - (a.m.votes || 0);
          if (dv !== 0) return dv;
          return a.i - b.i;
        })
        .slice(0, 10)
        .map((x) => x.m);
      if (topRows.length) setTopScorerLeaderboard(topRows);

      const rawAsiaStats =
        finalAsiaStatsRes?.code === 0 && Array.isArray(finalAsiaStatsRes?.data)
          ? finalAsiaStatsRes.data
          : unwrapApiList(finalAsiaStatsRes);
      const asiaRows = rawAsiaStats
        .map((row, i) => mapTeamsChampionsRow(row, i))
        .filter(Boolean)
        .sort((a, b) => (b.votes || 0) - (a.votes || 0));
      if (asiaRows.length) setAsiaLeaderboard(asiaRows);

      const rawAsiaCatalog =
        asiaTeamsRes?.code === 0 && Array.isArray(asiaTeamsRes?.data)
          ? asiaTeamsRes.data
          : unwrapApiList(asiaTeamsRes);
      const asiaMapped = rawAsiaCatalog.map(mapTeamFromApi).filter(Boolean);
      if (asiaMapped.length) setAsiaTeams(asiaMapped);
    } catch (e) {
      console.error(e);
      notification.error({
        message: 'Lỗi',
        description: 'Không tải được dữ liệu World Cup.'
      });
    } finally {
      setWcLoading(false);
    }
  }, [notification]);

  useEffect(() => {
    loadWcData();
  }, [loadWcData]);

  useImperativeHandle(ref, () => ({
    reloadTeams: loadWcData,
    reloadPlayers: loadWcData
  }));

  const requireAuth = () => {
    if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng đăng nhập để dự đoán.'
      });
      return false;
    }
    return true;
  };

  const onSubmitChampion = async () => {
    // if (!requireAuth()) return;
    const account = form?.getFieldValue?.('account')?.trim();
    if (!account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đăng nhập phía trên.'
      });
      return;
    }
    if (!selectedChampion) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng chọn 1 đội để dự đoán.'
      });
      return;
    }
    if (!IP || !FP) {
      notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
      return;
    }
    setIsChampionVoting(true);
    try {
      const res = await predictChampion({
        account,
        site: SITE,
        ip: IP?.ip ?? IP,
        fp: FP?.fp ?? FP,
        promo_id: 'default',
        teamCode: selectedChampion.id,
        teamName: selectedChampion.name
      });
      const ok = res?.code === 0 || res?.success === true;
      if (ok) {
        notification.success({ message: 'Thành công', description: 'Đã gửi dự đoán đội vô địch.' });
        localStorage.setItem('prediction_account', account);
        form?.setFieldsValue?.({
          championTeam: selectedChampion.name,
          championTeamCode: selectedChampion.id
        });
        await loadWcData();
      } else if (res?.valid === false) {
        notification.error({
          message: res?.title_mess || 'Lỗi',
          description: res?.text_mess || res?.message
        });
      } else {
        notification.error({
          message: 'Lỗi',
          description: res?.message || 'Gửi dự đoán thất bại.'
        });
      }
    } catch (e) {
      notification.error({
        message: 'Lỗi',
        description: e?.message || 'Gửi dự đoán thất bại.'
      });
    } finally {
      setIsChampionVoting(false);
    }
  };

  const onSubmitTopScorer = async () => {
    // if (!requireAuth()) return;
    const account = form?.getFieldValue?.('account')?.trim();
    if (!account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đăng nhập phía trên.'
      });
      return;
    }
    if (!selectedTopScorer) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng chọn 1 cầu thủ để dự đoán.'
      });
      return;
    }
    if (!IP || !FP) {
      notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
      return;
    }
    setIsTopScorerVoting(true);
    try {
      const res = await predictTopScorer({
        account,
        site: SITE,
        ip: IP?.ip ?? IP,
        fp: FP?.fp ?? FP,
        promo_id: 'default',
        playerCode: selectedTopScorer.id,
        playerName: selectedTopScorer.name
      });
      const ok = res?.code === 0 || res?.success === true;
      if (ok) {
        notification.success({ message: 'Thành công', description: 'Đã gửi dự đoán vua phá lưới.' });
        localStorage.setItem('prediction_account', account);
        form?.setFieldsValue?.({
          playerName: selectedTopScorer.name,
          playerCode: selectedTopScorer.id
        });
        await loadWcData();
      } else if (res?.valid === false) {
        notification.error({
          message: res?.title_mess || 'Lỗi',
          description: res?.text_mess || res?.message
        });
      } else {
        notification.error({
          message: 'Lỗi',
          description: res?.message || 'Gửi dự đoán thất bại.'
        });
      }
    } catch (e) {
      notification.error({
        message: 'Lỗi',
        description: e?.message || 'Gửi dự đoán thất bại.'
      });
    } finally {
      setIsTopScorerVoting(false);
    }
  };

  const onSubmitAsiaDeep = async () => {
    const account = form?.getFieldValue?.('account')?.trim();
    if (!account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đăng nhập phía trên.'
      });
      return;
    }
    if (!selectedAsia) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng chọn 1 đội châu Á để dự đoán.'
      });
      return;
    }
    if (!IP || !FP) {
      notification.warning({ message: 'Cảnh báo', description: 'Đang tải thông tin IP/FP.' });
      return;
    }
    setIsAsiaVoting(true);
    try {
      const res = await predictFinalAsiaTeam({
        account,
        site: SITE,
        ip: IP?.ip ?? IP,
        fp: FP?.fp ?? FP,
        promo_id: 'default',
        teamCode: selectedAsia.id,
        teamName: selectedAsia.name
      });
      const ok = res?.code === 0 || res?.success === true;
      if (ok) {
        notification.success({
          message: 'Thành công',
          description: 'Đã gửi dự đoán đội châu Á vào sâu nhất.'
        });
        localStorage.setItem('prediction_account', account);
        form?.setFieldsValue?.({
          finalAsiaTeam: selectedAsia.name,
          finalAsiaTeamCode: selectedAsia.id
        });
        await loadWcData();
      } else if (res?.valid === false) {
        notification.error({
          message: res?.title_mess || 'Lỗi',
          description: res?.text_mess || res?.message
        });
      } else {
        notification.error({
          message: 'Lỗi',
          description: res?.message || 'Gửi dự đoán thất bại.'
        });
      }
    } catch (e) {
      notification.error({
        message: 'Lỗi',
        description: e?.message || 'Gửi dự đoán thất bại.'
      });
    } finally {
      setIsAsiaVoting(false);
    }
  };

  if (wcLoading) {
    return (
      <div className="w-full py-10 text-center text-[#0A5AE1] font-semibold">Đang tải World Cup…</div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      {/* Tab strip — mobile: Figma 232:1421 (gap 5px, radius ~7px, icon–text ~8.7px, icon ~20px); md+: rộng hơn */}
      <div className="relative z-20 w-full rounded-t-[14px] border border-b-0 border-[#e2e8f0] bg-white/95">
        <div className="flex w-full min-w-0 justify-center overflow-x-auto px-2 py-3 md:px-3 md:py-4 [scrollbar-width:thin]">
          <div
            role="tablist"
            aria-label="Dự đoán World Cup"
            className={twMerge(
              // <md: hàng 1 = 2 tab (grid), hàng 2 = tab 3 căn giữa; md+: một hàng (contents gỡ wrapper)
              'flex w-full min-w-0 max-w-full flex-col gap-y-[4px] sm:gap-y-[5px]',
              'md:w-auto md:max-w-none md:flex-row md:flex-nowrap md:items-center md:justify-center md:gap-x-[16px] md:gap-y-0 lg:gap-x-[24px]'
            )}
          >
            <div className="grid min-w-0 w-full grid-cols-2 items-stretch gap-x-[4px] sm:gap-x-[5px] md:contents">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'champion'}
              onClick={() => setActiveTab('champion')}
              className={twMerge(
                'flex min-h-[34px] min-w-0 items-center justify-center gap-[6px] rounded-[7px] border-[0.58px] px-[6px] py-[6px] outline-none transition-all duration-200',
                'md:min-h-[50px] md:max-w-[min(100vw-2rem,349px)] md:flex-none md:basis-auto md:gap-[14px] md:rounded-[11px] md:border-[0.9px] md:px-5 md:py-2.5 lg:max-w-none',
                'border-white/40 font-bold uppercase leading-tight text-white',
                'text-[10px] tracking-[-0.02em] sm:text-[11px] md:text-base lg:text-[18px] focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#e8f4ff]',
                activeTab === 'champion'
                  ? 'bg-gradient-to-b from-[#008CFF] to-[#0DB6FF] shadow-[inset_0_0.58px_2.32px_1.16px_rgba(210,234,255,0.95),inset_0_0.58px_10.46px_1.16px_rgba(210,234,255,0.55)] md:shadow-[inset_0_1px_4px_rgba(210,234,255,0.95),inset_0_1px_16px_rgba(210,234,255,0.55)]'
                  : 'bg-gradient-to-b from-[#787878] to-[#B2B2B2] shadow-[inset_0_0.58px_2.32px_1.16px_rgba(210,234,255,0.45),inset_0_0.58px_10.46px_1.16px_rgba(0,0,0,0.08)] md:shadow-[inset_0_1px_4px_rgba(210,234,255,0.45),inset_0_1px_12px_rgba(0,0,0,0.08)]'
              )}
            >
              <span className="relative inline-flex h-[20px] w-[20px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/15 ring-1 ring-white/25 md:h-8 md:w-8 md:rounded-lg">
                <img
                  src="/assets/images/tab/icon_dvd.png"
                  alt=""
                  className="h-[80%] w-[80%] object-contain"
                  draggable={false}
                />
              </span>
              <span className="min-w-0 text-center leading-[1.12]">DỰ ĐOÁN ĐỘI VÔ ĐỊCH</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'top-scorer'}
              onClick={() => setActiveTab('top-scorer')}
              className={twMerge(
                'flex min-h-[34px] min-w-0 items-center justify-center gap-[6px] rounded-[7px] border-[0.58px] px-[6px] py-[6px] outline-none transition-all duration-200',
                'md:min-h-[50px] md:max-w-[min(100vw-2rem,349px)] md:flex-none md:basis-auto md:gap-[14px] md:rounded-[11px] md:border-[0.9px] md:px-5 md:py-2.5 lg:max-w-none',
                'border-white/40 font-bold uppercase leading-tight text-white',
                'text-[10px] tracking-[-0.02em] sm:text-[11px] md:text-base lg:text-[18px] focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#e8f4ff]',
                activeTab === 'top-scorer'
                  ? 'bg-gradient-to-b from-[#008CFF] to-[#0DB6FF] shadow-[inset_0_0.58px_2.32px_1.16px_rgba(210,234,255,0.95),inset_0_0.58px_10.46px_1.16px_rgba(210,234,255,0.55)] md:shadow-[inset_0_1px_4px_rgba(210,234,255,0.95),inset_0_1px_16px_rgba(210,234,255,0.55)]'
                  : 'bg-gradient-to-b from-[#787878] to-[#B2B2B2] shadow-[inset_0_0.58px_2.32px_1.16px_rgba(210,234,255,0.45),inset_0_0.58px_10.46px_1.16px_rgba(0,0,0,0.08)] md:shadow-[inset_0_1px_4px_rgba(210,234,255,0.45),inset_0_1px_12px_rgba(0,0,0,0.08)]'
              )}
            >
              <span className="relative inline-flex h-[20px] w-[20px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/15 ring-1 ring-white/25 md:h-8 md:w-8 md:rounded-lg">
                <img
                  src="/assets/images/tab/icon_vpl.png"
                  alt=""
                  className="h-[80%] w-[80%] object-contain"
                  draggable={false}
                />
              </span>
              <span className="min-w-0 text-center leading-[1.12]">DỰ ĐOÁN VUA PHÁ LƯỚI</span>
            </button>
            </div>
            <div className="flex w-full min-w-0 justify-center md:contents">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'asia-deep'}
              onClick={() => setActiveTab('asia-deep')}
              className={twMerge(
                'flex min-h-[34px] min-w-0 w-auto max-w-full items-center justify-center gap-[6px] rounded-[7px] border-[0.58px] px-[10px] py-[6px] outline-none transition-all duration-200',
                'md:min-h-[50px] md:max-w-[min(100vw-2rem,349px)] md:flex-none md:basis-auto md:gap-[14px] md:rounded-[11px] md:border-[0.9px] md:px-5 md:py-2.5 lg:max-w-none',
                'border-white/40 font-bold uppercase leading-tight text-white',
                'text-[10px] tracking-[-0.02em] sm:text-[11px] md:text-base lg:text-[18px] focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#e8f4ff]',
                activeTab === 'asia-deep'
                  ? 'bg-gradient-to-b from-[#008CFF] to-[#0DB6FF] shadow-[inset_0_0.58px_2.32px_1.16px_rgba(210,234,255,0.95),inset_0_0.58px_10.46px_1.16px_rgba(210,234,255,0.55)] md:shadow-[inset_0_1px_4px_rgba(210,234,255,0.95),inset_0_1px_16px_rgba(210,234,255,0.55)]'
                  : 'bg-gradient-to-b from-[#787878] to-[#B2B2B2] shadow-[inset_0_0.58px_2.32px_1.16px_rgba(210,234,255,0.45),inset_0_0.58px_10.46px_1.16px_rgba(0,0,0,0.08)] md:shadow-[inset_0_1px_4px_rgba(210,234,255,0.45),inset_0_1px_12px_rgba(0,0,0,0.08)]'
              )}
            >
              <span className="relative inline-flex h-[20px] w-[20px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/15 ring-1 ring-white/25 md:h-8 md:w-8 md:rounded-lg">
                <img
                  src="/assets/images/tab/star.png"
                  alt=""
                  className="h-[80%] w-[80%] object-contain"
                  draggable={false}
                />
              </span>
              <span className="min-w-0 whitespace-normal text-center leading-[1.1] md:leading-[1.12]">
                ĐỘI CHÂU Á VÀO SÂU NHẤT
              </span>
            </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="w-full rounded-b-[14px] overflow-hidden border border-t-0 border-[#e2e8f0]"
        style={{
          background:
            'linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), rgba(255, 255, 255, 0.5)',
          boxShadow:
            '0px 0px 22.2698px #92DEFF, 0px 8px 12px rgba(0, 0, 0, 0.08), inset 2.02822px 2.02822px 2.02822px -2.02822px #FFFFFF, inset -2.02822px -2.02822px 2.02822px -2.02822px #FFFFFF',
          backdropFilter: 'blur(10.1411px)',
          WebkitBackdropFilter: 'blur(10.1411px)'
        }}
      >
        <div
          className={twMerge('px-1 pb-3 md:px-3', disabled ? 'pointer-events-none opacity-70' : '')}
        >
          {activeTab === 'champion' ? (
            <ChampionPredictionTab
              championLeaderboard={championLeaderboard}
              championTeams={championTeams}
              filteredChampionTeams={filteredChampionTeams}
              championQuery={championQuery}
              setChampionQuery={(value) => {
                setIsChampionAutoFilled(false);
                setChampionQuery(value);
              }}
              championCountryFilter={championCountryFilter}
              setChampionCountryFilter={setChampionCountryFilter}
              championCountryOptions={championCountryOptions}
              isChampionVoting={isChampionVoting}
              onSubmitChampion={onSubmitChampion}
              selectedChampionId={selectedChampionId}
              setSelectedChampionId={(id) => {
                setSelectedChampionId(id);
                const team = championTeams.find((t) => String(t.id) === String(id));
                if (team?.name) {
                  setChampionQuery(team.name);
                  setIsChampionAutoFilled(true);
                }
              }}
              fillChampionQueryFromPick={(name) => {
                setIsChampionAutoFilled(true);
                setChampionQuery(name);
              }}
            />
          ) : activeTab === 'top-scorer' ? (
            <PlayerPredictionTab
              title="Dự đoán vua phá lưới"
              leaderboard={topScorerLeaderboard}
              players={filteredTopScorerPlayers}
              catalogPlayers={topScorerPlayersList}
              query={topScorerQuery}
              setQuery={(value) => {
                setIsTopScorerAutoFilled(false);
                setTopScorerQuery(value);
              }}
              countryFilter={topScorerCountryFilter}
              setCountryFilter={setTopScorerCountryFilter}
              countryOptions={playerCountryOptions}
              selectedId={selectedTopScorerId}
              setSelectedId={(id) => {
                setSelectedTopScorerId(id);
                const player = topScorerPlayersList.find((p) => String(p.id) === String(id));
                if (player?.name) {
                  setTopScorerQuery(player.name);
                  setIsTopScorerAutoFilled(true);
                }
              }}
              loading={isTopScorerVoting}
              onSubmit={onSubmitTopScorer}
              fillPlayerQueryFromPick={(name) => {
                setIsTopScorerAutoFilled(true);
                setTopScorerQuery(name);
              }}
            />
          ) : (
            <ChampionPredictionTab
              votePanelTitle="Dự đoán đội châu Á vào sâu nhất"
              searchPlaceholder="Tìm đội châu Á"
              championLeaderboard={asiaLeaderboard}
              championTeams={asiaTeams}
              filteredChampionTeams={filteredAsiaTeams}
              championQuery={asiaQuery}
              setChampionQuery={(value) => {
                setIsAsiaAutoFilled(false);
                setAsiaQuery(value);
              }}
              championCountryFilter={asiaCountryFilter}
              setChampionCountryFilter={setAsiaCountryFilter}
              championCountryOptions={asiaCountryOptions}
              isChampionVoting={isAsiaVoting}
              onSubmitChampion={onSubmitAsiaDeep}
              selectedChampionId={selectedAsiaId}
              setSelectedChampionId={(id) => {
                setSelectedAsiaId(id);
                const team = asiaTeams.find((t) => String(t.id) === String(id));
                if (team?.name) {
                  setAsiaQuery(team.name);
                  setIsAsiaAutoFilled(true);
                }
              }}
              fillChampionQueryFromPick={(name) => {
                setIsAsiaAutoFilled(true);
                setAsiaQuery(name);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default WorldCupPredictionBlock;
